import { forwardRef, InputHTMLAttributes, ChangeEvent } from "react";
import { clsx } from "clsx";
import {
  formatCPF,
  formatCNPJ,
  formatDocument,
  formatPhone,
  formatCEP,
  capitalizeName,
  formatCurrencyInput,
  validateCPF,
  validateCNPJ,
  validateEmail,
} from "@/utils/formatters";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  format?:
    | "cpf"
    | "cnpj"
    | "document"
    | "phone"
    | "cep"
    | "name"
    | "email"
    | "currency";
  validateOnBlur?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      id,
      format,
      validateOnBlur = true,
      onChange,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const inputId = id || props.name;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      // Aplicar formatação baseada no tipo
      if (format) {
        const input = e.target;
        let value = input.value;

        switch (format) {
          case "cpf":
            value = formatCPF(value);
            break;
          case "cnpj":
            value = formatCNPJ(value);
            break;
          case "document":
            value = formatDocument(value);
            break;
          case "phone":
            value = formatPhone(value);
            break;
          case "cep":
            value = formatCEP(value);
            break;
          case "name":
            value = capitalizeName(value);
            break;
          case "email":
            value = value.toLowerCase().trim();
            break;
          case "currency":
            value = formatCurrencyInput(value);
            break;
        }

        // Atualiza o valor do input diretamente
        input.value = value;

        // Cria evento nativo para o react-hook-form
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value",
        )?.set;
        nativeInputValueSetter?.call(input, value);
        const inputEvent = new Event("input", { bubbles: true });
        input.dispatchEvent(inputEvent);

        // Também chama o onChange das props para callbacks customizados
        if (onChange) {
          const newEvent = {
            ...e,
            target: { ...input, value },
            currentTarget: { ...input, value },
          };
          onChange(newEvent as ChangeEvent<HTMLInputElement>);
        }
      } else {
        if (onChange) {
          onChange(e);
        }
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (validateOnBlur && format) {
        const value = e.target.value;
        let isValid = true;
        let errorMsg = "";

        switch (format) {
          case "cpf":
            if (value && !validateCPF(value)) {
              isValid = false;
              errorMsg = "CPF inválido";
            }
            break;
          case "cnpj":
            if (value && !validateCNPJ(value)) {
              isValid = false;
              errorMsg = "CNPJ inválido";
            }
            break;
          case "email":
            if (value && !validateEmail(value)) {
              isValid = false;
              errorMsg = "Email inválido";
            }
            break;
        }

        // Se houver erro de validação, você pode usar um callback para informar o pai
        if (!isValid && errorMsg) {
          // O componente pai deve gerenciar o estado do erro
          console.warn(errorMsg);
        }
      }

      if (onBlur) {
        onBlur(e);
      }
    };

    // Define maxLength baseado no formato (se não estiver explicitamente definido)
    const getMaxLength = () => {
      if (props.maxLength) return props.maxLength;

      switch (format) {
        case "cpf":
          return 14; // 000.000.000-00
        case "cnpj":
          return 18; // 00.000.000/0000-00
        case "document":
          return 18; // Maior formato (CNPJ)
        case "phone":
          return 15; // (00) 00000-0000
        case "cep":
          return 9; // 00000-000
        default:
          return undefined;
      }
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-text-secondary mb-2"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            {...props}
            className={clsx(
              "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-text-primary placeholder-text-muted",
              "focus:outline-none focus:border-accent-primary/50 focus:ring-2 focus:ring-accent-primary/20",
              "transition-all duration-300",
              leftIcon && "pl-12",
              rightIcon && "pr-12",
              error &&
                "border-red-500/50 focus:border-red-500 focus:ring-red-500/20",
              className,
            )}
            maxLength={getMaxLength()}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {rightIcon && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="mt-1.5 text-sm text-red-400">{error}</p>}
        {hint && !error && (
          <p className="mt-1.5 text-sm text-text-muted">{hint}</p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
